import type { MedicalDocument } from './cloudLabDocuments';
import type { LabResult } from './localData';

// Search stays entirely on-device/in the already loaded family dataset. It is
// deliberately a literal text match over names confirmed from the source
// report, never a clinical synonym generator.
export function normalizeLabSearchText(value: string | undefined): string {
  return (value || '')
    .toLocaleLowerCase('ru-RU')
    .replace(/ё/g, 'е')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

function matchesFields(fields: Array<string | undefined>, query: string): boolean {
  const terms = normalizeLabSearchText(query).split(' ').filter(Boolean);
  if (!terms.length) return true;
  const haystack = normalizeLabSearchText(fields.filter(Boolean).join(' '));
  return terms.every(term => haystack.includes(term));
}

export function labResultSearchFields(result: LabResult): Array<string | undefined> {
  return [
    result.analyte,
    result.originalName,
    result.russianName,
    result.internationalName,
    result.canonicalCode,
    ...(result.aliases || []),
    result.panel,
    result.labName,
    result.unit,
    result.notes,
  ];
}

export function matchesLabResultSearch(result: LabResult, query: string): boolean {
  return matchesFields(labResultSearchFields(result), query);
}

const COUNTRY_SEARCH_LABELS = {
  russia: 'россия russian',
  israel: 'израиль israel',
  other: 'другая other',
} as const;

export function matchesLabDocumentSearch(document: MedicalDocument, results: LabResult[], query: string): boolean {
  const linkedResultFields = results
    .filter(result => result.documentId === document.id)
    .flatMap(labResultSearchFields);
  return matchesFields([
    document.originalName,
    document.labName,
    document.takenOn,
    document.country ? COUNTRY_SEARCH_LABELS[document.country] : undefined,
    document.laboratoryConclusion,
    ...linkedResultFields,
  ], query);
}
